import { useRef, useState, type Dispatch } from 'react';
import {
  type FlightExplorerOffer,
  EXPLORER_PAGE_LIMIT,
  flightSearchFailureCause,
  type FlightExplorerRequest,
  type FlightSearchFn,
  type FlightSearchParams,
} from './search_source.js';

import { useExplorerEnv } from './explorer_env.js';

import {
  useOfferFares,
  type FlightPinControl,
  type OfferFares,
} from '../results/offer/offer_list.js';

import {
  editedRequest,
  explorerConfirm,
  faresLegOf,
  isFaresStage,
  isFinalPick,
  legSearchParams,
  reduceExplorer,
  sameTripGeometry,
  tripGeometryOf,
  type ExplorerConfirm,
  type ExplorerEvent,
  type ExplorerPick,
  type ExplorerState,
} from './explorer_state.js';

import { makeExplorerRetry, type ExplorerRetryIo } from './explorer_retry.js';
import { useRollbackCabinEcho } from './explorer_seams.js';
import type { FlightExplorerPageProps } from './flight_explorer_page.js';

import { activeOffersOfLeg } from './explorer_steps.js';

/*
 * O ESTADO do explorer: a busca e o refine (`useExplorerIo`), as tarifas, o pick
 * e os pins. Mora fora do `explorer.tsx` para ele caber no teto de linhas antes de
 * mudar de pacote (#4997, C0b); a tela fica no `explorer_body.tsx`.
 */

/**
 * O espelho de IO do reducer puro: cada disparo de busca incrementa o seq local
 * JUNTO do evento `*_started` (que incrementa o do estado) — é o que faz a
 * resposta stale ser detectável DENTRO do reducer, sem o componente decidir nada.
 */
export function useExplorerIo(
  fs: FlightSearchFn | undefined,
  state: ExplorerState,
  send: Dispatch<ExplorerEvent>,
  onCabinChange?: FlightExplorerPageProps['onCabinChange'],
) {
  const seqsRef = useRef<number[]>([...state.seqs]);
  const { track } = useExplorerEnv();
  useRollbackCabinEcho(state, onCabinChange);

  const bumpSeq = (leg: number): number => {
    const seq = (seqsRef.current[leg] ?? 0) + 1;
    seqsRef.current[leg] = seq;
    return seq;
  };

  /**
   * O ÚNICO caminho de fracasso de busca (#2926). Antes cada `.then` tinha o seu
   * `() => send({ t: 'search_failed', … })` e o erro morria sem inspeção: rede
   * caída na janela de 40s, HTTP não-ok e corpo fora do contrato colapsavam no
   * mesmo evento, e o bug precisou ser reconstituído trace a trace porque o
   * cliente não deixava rastro nenhum. A causa vem NO erro (a borda de dados a
   * marca) e a duração sai do relógio da própria tentativa — é ela que diz se a
   * falha veio da cauda de 40s+ ou de um tombo imediato.
   */
  const searchFailed = (leg: number, seq: number, startedAt: number) => (error: unknown) => {
    // A rejeição de uma busca que JÁ PERDEU a posse da perna morre aqui, como a
    // da página em `loadMoreReturns`: o reducer sempre a descartou (o guarda de
    // `seq` do `search_failed`), mas a telemetria não — e contar o fracasso de
    // uma busca abandonada infla justamente a régua que o #2926 criou para medir
    // o fracasso que a bancada MOSTRA.
    if ((seqsRef.current[leg] ?? 0) !== seq) return;
    const cause = flightSearchFailureCause(error);
    track('flight_search_failed', { cause, durationMs: Date.now() - startedAt });
    send({ t: 'search_failed', leg, seq, cause });
  };

  /** UMA perna (fan-out do multi + re-busca da faixa no multi). */
  const runLegSearch = (leg: number, params: FlightSearchParams) => {
    if (!fs) return;
    const seq = bumpSeq(leg);
    const startedAt = Date.now();
    send({ t: 'leg_search_started', leg, params });
    fs.search(params).then(
      (r) => {
        send({ t: 'search_ok', leg, seq, offers: r.offers, cabinPolicy: r.cabinPolicy });
      },
      searchFailed(leg, seq, startedAt),
    );
  };

  /** Re-busca INTEIRA: o pedido é RE-FORMADO pelos params (o modo segue o
   *  returnDate) e sobra UMA perna — inclusive quando o pedido corrente era um
   *  roteiro multi e a pessoa apertou `Buscar` numa aba binária. `onLanded` (a
   *  busca vencida refeita, #5588) só roda se a busca ainda é a da perna. */
  const startSearch = (
    params: FlightSearchParams,
    onLanded?: (offers: FlightExplorerOffer[]) => void,
    keepScene?: boolean,
  ) => {
    if (!fs) return;
    const seq = bumpSeq(0);
    const startedAt = Date.now();
    send({ t: 'search_started', params, ...(keepScene ? { keepScene } : {}) });
    fs.search(params).then(
      (r) => {
        send({ t: 'search_ok', leg: 0, seq, offers: r.offers, cabinPolicy: r.cabinPolicy });
        if (onLanded && (seqsRef.current[0] ?? 0) === seq) onLanded(r.offers);
      },
      searchFailed(0, seq, startedAt),
    );
  };
  const runSearch: ExplorerRetryIo['runSearch'] = (params, onLanded) => {
    startSearch(params, onLanded);
  };
  /** A cabine da coluna no one_way/RT: a mesma re-busca inteira, mas a cena em
   *  tela fica guardada para a falha ou o pouso vazio (#6438). */
  const runCabinSearch = (params: FlightSearchParams) => {
    startSearch(params, undefined, true);
  };

  /** O ROTEIRO foi re-editado na faixa: o pedido renasce com as pernas novas e o
   *  fan-out refaz UMA busca one-way por trecho. Os params saem do pedido DERIVADO
   *  (`editedRequest`, a mesma conta do reducer) e não do corrente: neste turno o
   *  commit do evento ainda não chegou, e ler `state.request` mandaria a busca com
   *  as datas velhas. O seq de cada perna sobe junto do evento — é o que faz o
   *  pouso de uma perna do roteiro ANTERIOR ser descartável. */
  const runLegsEdited = (legs: FlightExplorerRequest['legs']) => {
    if (!fs) return;
    const next = editedRequest(state.request, legs);
    send({ t: 'legs_edited', legs });
    legs.forEach((_, i) => {
      const seq = bumpSeq(i);
      const startedAt = Date.now();
      fs.search(legSearchParams(next, i)).then(
        (r) => {
          send({ t: 'search_ok', leg: i, seq, offers: r.offers, cabinPolicy: r.cabinPolicy });
        },
        searchFailed(i, seq, startedAt),
      );
    });
  };

  /** Fase 1 do RT: o VOO da ida escolhido dispara o return-search da MESMA
   *  sessão. Sem tarifa aqui por decisão de produto: a escada da fase 1 rodava
   *  na captura one-way da ida, cujos preços não são os do par — as tarifas
   *  reais (as da matriz do par) só existem na fase 2, e é lá que se escolhe. */
  const chooseOutbound = (offerId: string) => {
    if (!fs) return;
    const seq = bumpSeq(1);
    const startedAt = Date.now();
    send({ t: 'outbound_chosen', offerId });
    fs.returnSearch(offerId, { number: 1, limit: EXPLORER_PAGE_LIMIT }).then(
      (r) => {
        send({
          t: 'search_ok',
          leg: 1,
          seq,
          offers: r.offers,
          ...(r.page ? { page: r.page } : {}),
        });
      },
      searchFailed(1, seq, startedAt),
    );
  };

  /** Página seguinte das VOLTAS (fase 2 do RT) — o refine é congelado nessa fase,
   *  então o scroll pagina pelo próprio return-search. A posse é o seq da perna:
   *  trocar a ida re-buscou as voltas e a página velha não pode pousar. */
  const loadMoreReturns = () => {
    if (!fs) return;
    const outbound = state.chosen[0];
    const current = state.legs[1];
    if (!outbound || current?.phase !== 'ready' || !current.page?.hasNext || current.loadingMore)
      return;
    const seq = seqsRef.current[1] ?? 0;
    send({ t: 'page_started', leg: 1 });
    fs.returnSearch(outbound.offerId, {
      number: current.page.number + 1,
      limit: EXPLORER_PAGE_LIMIT,
    }).then(
      (r) => {
        if ((seqsRef.current[1] ?? 0) !== seq) return;
        send({
          t: 'refine_ok',
          leg: 1,
          offers: r.offers,
          append: true,
          ...(r.page ? { page: r.page } : {}),
        });
      },
      () => {
        if ((seqsRef.current[1] ?? 0) !== seq) return;
        send({ t: 'page_failed', leg: 1 });
      },
    );
  };

  /**
   * A busca VIVA por cima de uma perna semeada: mesmo pedido, seq novo. O evento é
   * `refresh_started` e não `leg_search_started` porque aquele troca a perna por
   * `loading` — apagaria justamente o primeiro quadro que a semente existe pra dar,
   * e a tela voltaria ao esqueleto de 12–40s que este caminho remove.
   */
  const refreshLeg = (leg: number, params: FlightSearchParams) => {
    if (!fs) return;
    const seq = bumpSeq(leg);
    const startedAt = Date.now();
    send({ t: 'refresh_started', leg });
    fs.search(params).then(
      (r) => {
        send({ t: 'search_ok', leg, seq, offers: r.offers, cabinPolicy: r.cabinPolicy });
      },
      searchFailed(leg, seq, startedAt),
    );
  };

  /** Re-dispara a busca que a fase corrente perdeu (erro → Tentar de novo). */
  const retry = () => {
    makeExplorerRetry(state, { runLegSearch, runSearch, chooseOutbound }, send)();
  };

  return {
    runLegSearch,
    runSearch,
    runCabinSearch,
    runLegsEdited,
    refreshLeg,
    chooseOutbound,
    loadMoreReturns,
    retry,
  };
}

/**
 * O tariff é lido NESTA fase?
 *
 * Só na fase de TARIFA (`isFaresStage`), em qualquer modo: as fases de lista
 * selecionam o VOO (no RT porque a escada rodaria na captura one-way da ida,
 * com preços que não são os do par; em todo modo porque desde a padronização
 * de 30/09, #3875, a escolha de tarifa é a tela "Escolha as tarifas", nunca a
 * escada inline embaixo da linha).
 *
 * A resposta liga DUAS coisas de uma vez, e é de propósito: o `OfferListBody` lê
 * `fares.enabled` pra decidir entre escada e select-mode, e o `useOfferFares` a
 * usa pra autorizar o fetch do `autoExpandId`. As duas perguntas têm a mesma
 * resposta — "esta fase precisa do tariff?".
 */
function faresEnabled(hasPort: boolean, state: ExplorerState): boolean {
  return hasPort && isFaresStage(state);
}

/**
 * A escada de tarifas da fase corrente — `useOfferFares` com o insumo certo.
 * Embrulho (mesmo espírito de `cabinSeam`/`pillNodeFor`) porque o
 * `FlightExplorer` vive no teto de complexidade e de linhas.
 *
 * O primeiro parâmetro do hook só alimenta o efeito do `autoExpandId` (a escada
 * em si é indexada por `offerId`), e na fase de tarifa o pick PRECISA ser
 * achado ali pra o fetch do tariff sair — a lista visível pode tê-lo escondido
 * atrás de um corte, então o insumo passa a ser a lista inteira da perna.
 */
export function useExplorerFares(
  state: ExplorerState,
  rows: FlightExplorerOffer[],
): OfferFares<FlightExplorerOffer> {
  const { tariff, classify } = useExplorerEnv();
  const leg = faresLegOf(state);
  const pickId = isFaresStage(state) ? state.chosen[leg]?.offerId : undefined;
  return useOfferFares(pickId ? activeOffersOfLeg(state, leg) : rows, {
    enabled: faresEnabled(Boolean(tariff), state),
    ...(pickId ? { autoExpandId: pickId } : {}),
    tariff,
    classify,
  });
}

/**
 * O pick da tela de tarifas fecha a perna corrente (as fases de lista não
 * passam por aqui: lá a linha SELECIONA o voo). A escada do hook FECHA junto:
 * perna resolvida com painel aberto era o "2 de 2 escolhidos" com a escolha
 * ainda em cena.
 *
 * Quando o pick é o ÚLTIMO trecho pendente (`isFinalPick`), o MESMO clique
 * também confirma — o rodapé da escada já convidava a isso (`fareCtaFor`). O
 * estado pós-pick é calculado com o MESMO reducer (`reduceExplorer`): o
 * `useReducer` ainda não aplicou o evento neste tick, e o confirm não pode ler
 * um `state` velho. O guarda de `sentRef` é o que impede um clique repetido
 * (ex.: escada reaberta após `resumeSeq`) de reconfirmar sem que o host tenha
 * re-armado o single-fire. Extraído (mesmo padrão de `cabinSeam`/
 * `pillNodeFor`) porque o `FlightExplorer` já está no teto de linhas.
 *
 * `isFinalPick` true e `confirm` null NÃO é impossível: pernas do MESMO
 * roteiro não deveriam divergir de moeda (todas saem do MESMO BFF), mas o
 * `sumMoney` do braço multi (`explorer_state.ts`) já se cala por construção
 * quando isso acontece — e é esse silêncio real, não uma hipótese, que produz
 * o dead-end (CodeRabbit #2174). Por isso o `collapse()` só roda quando a
 * escada TEM pra onde ir (perna intermediária) ou quando o confirm de fato
 * fechou: com `confirm` nulo o pick FICA gravado (`send(ev)` já rodou), mas a
 * escada segue ABERTA — é o único caminho de saída (trocar a tarifa do
 * trecho, ou removê-lo) — e `confirm_unavailable` acende o aviso visível
 * (`state.confirmStuck`), já que não há mais CTA de header como rede de
 * segurança (morreu na barra única). O reducer, não este componente, decide
 * quando o aviso apaga de novo (`pick_removed`, um novo `fares_picked`, ou
 * qualquer evento que reforme a busca) — ver `explorer_state.ts`.
 */
export function takePickFor(deps: {
  state: ExplorerState;
  fares: OfferFares<FlightExplorerOffer>;
  send: Dispatch<ExplorerEvent>;
  sentRef: { current: boolean };
  readOnly?: boolean;
  draftLock: boolean;
  onConfirm: (confirm: ExplorerConfirm) => void;
}): (pick: Required<ExplorerPick>) => void {
  const { state, fares, send, sentRef, readOnly, draftLock, onConfirm } = deps;
  return (pick) => {
    const ev = { t: 'fares_picked', leg: state.activeLeg, pick } as const;
    send(ev);
    if (!isFinalPick(state)) {
      fares.collapse();
      return;
    }
    const confirm = explorerConfirm(reduceExplorer(state, ev));
    if (!confirm) {
      send({ t: 'confirm_unavailable' });
      return;
    }
    fares.collapse();
    if (sentRef.current || readOnly || draftLock) return;
    sentRef.current = true;
    send({ t: 'confirmed' });
    onConfirm(confirm);
  };
}

/** O que um clique na LINHA significa: nas fases do round trip, a ida dispara
 *  o return-search da mesma sessão e o PAR fecha a lista; nos outros modos o
 *  voo da perna ativa fecha a lista — e nos três casos entra a tela de
 *  tarifas, sem ida à rede (o tariff é o do ref recém-escolhido). Fábrica (e
 *  não um lambda no corpo) porque o `FlightExplorer` vive no teto de linhas. */
export function makePickFlight(
  state: ExplorerState,
  io: ReturnType<typeof useExplorerIo>,
  send: Dispatch<ExplorerEvent>,
): (offer: FlightExplorerOffer) => void {
  return (offer) => {
    if (state.request.mode !== 'round_trip') {
      send({ t: 'flight_chosen', leg: state.activeLeg, offerId: offer.offerId });
    } else if (state.activeLeg === 0) io.chooseOutbound(offer.offerId);
    else send({ t: 'return_chosen', offerId: offer.offerId });
  };
}

/**
 * O estado + as derivações do pino "comprar depois" (#2294), fora do corpo do
 * `FlightExplorer` (que vive no teto de complexidade/linhas). O filtro "só
 * fixados" é view-state da COLUNA — não toca o reducer (que congela no
 * `confirmed`). Regras:
 *  - `pin` desligado em `frozen` (checkout): a linha já perde os gestos ali.
 *  - `pinnedCount` conta os fixados VIVOS na lista corrente — um fixado que saiu
 *    dos resultados (outra perna, preço/assento que mudou) não conta: é a
 *    leitura honesta da PERECIBILIDADE da oferta (não há backend que a salve).
 *  - `showPinned` cai sozinho quando o último fixado da lista sai, então a
 *    coluna nunca fica presa num vazio "só fixados" nem o chip mente "(0)".
 */
export function useExplorerPins(
  pins: FlightPinControl<FlightExplorerOffer> | undefined,
  frozen: boolean,
  rows: FlightExplorerOffer[],
): {
  pin: FlightPinControl<FlightExplorerOffer> | undefined;
  displayRows: FlightExplorerOffer[];
  pinnedFilter: { count: number; active: boolean; onToggle: () => void } | undefined;
} {
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const pin = frozen ? undefined : pins;
  const pinnedCount = pin ? rows.filter((o) => pin.isPinned(o.offerId)).length : 0;
  const showPinned = pinnedOnly && pinnedCount > 0;
  const displayRows = showPinned && pin ? rows.filter((o) => pin.isPinned(o.offerId)) : rows;
  const pinnedFilter =
    pin && pinnedCount > 0
      ? {
          count: pinnedCount,
          active: showPinned,
          onToggle: () => {
            setPinnedOnly((v) => !v);
          },
        }
      : undefined;
  return { pin, displayRows, pinnedFilter };
}

/** Divergência AO VIVO: o pedido corrente (só muda com Buscar) contra o de
 *  abertura (a prop `request` é o roteiro do host e não muda). O comparador é o
 *  MESMO do galho do confirm — a tela nunca pode avisar uma coisa e o payload
 *  despachar outra. */
export function replaceHintOf(
  request: FlightExplorerRequest,
  state: ExplorerState,
  replacesMultiCard: boolean | undefined,
): 'trip' | 'trip_picks' | null {
  const diverged = !sameTripGeometry(tripGeometryOf(request), tripGeometryOf(state.request));
  if (!replacesMultiCard || !diverged) return null;
  return state.picksDiscarded ? 'trip_picks' : 'trip';
}

/** A cabine da COLUNA: busca NOVA sem re-formar o pedido (rota, datas e forma
 *  ficam onde estão — quem mexe nelas é a faixa). No multi isso é a perna ativa:
 *  trocar a cabine não pode colapsar um roteiro de N trechos em um. */
export function researchFor(
  io: ReturnType<typeof useExplorerIo>,
  state: ExplorerState,
  bandLeg: number,
): (p: FlightSearchParams) => void {
  return (p) => {
    if (state.request.mode === 'multi_leg') io.runLegSearch(bandLeg, p);
    else io.runCabinSearch(p);
  };
}
